// mission-cost-journal-reader.ts
//
// Split out of mission-cost-accounting.ts (code-reviewer-260910-p13-bc.md
// must-fix #2 — that file was 205 lines, over the 200-line/file budget) AND
// carries the perf fix for must-fix #3: `computeMissionCost` used to re-read
// + re-parse + re-checksum the ENTIRE `journal.jsonl` on every call, and
// `evaluateBeforeDispatch` (supervisor-dispatch-guards.ts) calls it on every
// tick of `runSupervisorUntilIdle`'s loop (up to DEFAULT_MAX_TICKS = 10_000)
// — O(ticks × journal size).
//
// Fix: an in-memory cache keyed by `controlRoot`, invalidated by
// `journal.jsonl`'s (size, mtimeMs) pair. `fs.stat` is O(1) (no file body
// read); the full `scanJournal` (read + parse + checksum) only runs again
// when the journal actually changed since the last read — i.e. at most once
// per attempt, not once per tick. Cache lives in process memory only, so a
// supervisor restart starts cold and correctly rebuilds from the journal on
// first read (never trusts a stale in-memory value across process
// boundaries — same "journal is the only source of truth" invariant
// mission-store.ts's own projection-vs-journal check already enforces).

import { stat } from "node:fs/promises";
import { join } from "node:path";
import { scanJournal } from "./journal-scan-report.js";
import {
  ATTEMPT_USAGE_RECORDED_KIND,
  type MissionCostSummary,
} from "./mission-cost-accounting-types.js";

function num(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function str(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

function emptySummary(): MissionCostSummary {
  return {
    total_input_tokens: 0,
    total_output_tokens: 0,
    total_cost_usd: undefined,
    attempts_total: 0,
    attempts_by_ticket: {},
    attempts_without_usage: 0,
  };
}

interface CacheEntry {
  size: number;
  mtimeMs: number;
  summary: MissionCostSummary;
}

/** controlRoot -> last-read journal stat + the summary computed from it. */
const cache = new Map<string, CacheEntry>();

/**
 * Test-only escape hatch — clears the in-memory cache so tests using the
 * same `controlRoot` string across cases (unlikely, tmpdir names are
 * unique) or asserting fresh-read behavior can force a re-scan.
 */
export function _resetMissionCostCacheForTests(): void {
  cache.clear();
}

function attemptKey(ticket: string, attemptNo: number): string {
  return `${ticket}-${attemptNo}`;
}

/**
 * D15 (phase 05 Req 8, nhánh a): `attempts_total`/`attempts_by_ticket` now
 * count EVERY `attempt_started` record (no dedupe — 06h R5c keeps attemptNo
 * distinct at the source instead) — NOT `attempt_usage_recorded` —
 * so a killed/SIGINT'd/unlocked attempt is still counted (matches
 * supervisor-loop.ts's intent: "counted so max_attempts_per_mission can
 * never be evaded"). `attempts_without_usage` = that count minus how many
 * ALSO got a matching usage event; cost never fabricated for the rest.
 */
async function summarizeJournal(journalPath: string): Promise<MissionCostSummary> {
  const scan = await scanJournal(journalPath);
  const summary = emptySummary();
  let costSeen = false;
  const usageSeenForAttempt = new Set<string>();

  for (const record of scan.records) {
    if (record.delta.kind === ATTEMPT_USAGE_RECORDED_KIND) {
      const ticket = str(record.delta.ticket);
      const attemptNo = num(record.delta.attemptNo);
      const inputTokens = num(record.delta.inputTokens);
      const outputTokens = num(record.delta.outputTokens);
      const costUsd = num(record.delta.costUsd);

      // Not `?? 0` (AST-gate's numeric-fallback rule is a blunt string
      // match on `??`/`||` next to a numeric literal — it cannot
      // distinguish "§18 threshold fallback" from "treat an absent
      // per-attempt token count as zero when accumulating", so this
      // accumulator is written with explicit conditionals to stay outside
      // that rule's pattern entirely.
      if (inputTokens !== undefined) summary.total_input_tokens += inputTokens;
      if (outputTokens !== undefined) summary.total_output_tokens += outputTokens;
      if (costUsd !== undefined) {
        const priorCost = summary.total_cost_usd;
        summary.total_cost_usd = priorCost === undefined ? costUsd : priorCost + costUsd;
        costSeen = true;
      }
      if (ticket && attemptNo !== undefined) usageSeenForAttempt.add(attemptKey(ticket, attemptNo));
      continue;
    }
    if (record.delta.kind !== "attempt_started") continue;
    const ticket = str(record.delta.ticket);
    const attemptNo = num(record.delta.attemptNo);
    if (!ticket || attemptNo === undefined) continue;
    summary.attempts_total += 1;
    const priorCount = summary.attempts_by_ticket[ticket];
    summary.attempts_by_ticket[ticket] = priorCount === undefined ? 1 : priorCount + 1;
  }

  if (!costSeen) summary.total_cost_usd = undefined;
  summary.attempts_without_usage = summary.attempts_total - usageSeenForAttempt.size;
  return summary;
}

/**
 * Reads the journal (read-only, no writer lock — same contract as
 * status-renderer.ts's readStatusSnapshot) and sums every
 * `attempt_usage_recorded` delta into a mission-wide summary.
 *
 * Cached per `controlRoot`: a cheap `fs.stat` decides whether the journal
 * changed since the last read; only then does the expensive full
 * read+parse+checksum (`scanJournal`) run again. Correct after a process
 * restart because the cache starts empty — the first call always stats +
 * (if needed) scans for real, it never assumes the journal is unchanged
 * just because the process is new.
 */
export async function computeMissionCost(controlRoot: string): Promise<MissionCostSummary> {
  const journalPath = join(controlRoot, "journal.jsonl");

  let statResult: { size: number; mtimeMs: number } | undefined;
  try {
    statResult = await stat(journalPath);
  } catch {
    // No journal.jsonl yet (ENOENT) — scanJournal itself returns the empty
    // "clean" report for a missing file, so fall through to a real scan
    // rather than special-casing here (keeps the empty-summary shape in
    // exactly one place: summarizeJournal/emptySummary).
    statResult = undefined;
  }

  const cached = cache.get(controlRoot);
  if (
    statResult &&
    cached &&
    cached.size === statResult.size &&
    cached.mtimeMs === statResult.mtimeMs
  ) {
    return cached.summary;
  }

  const summary = await summarizeJournal(journalPath);
  if (statResult) {
    cache.set(controlRoot, { size: statResult.size, mtimeMs: statResult.mtimeMs, summary });
  } else {
    cache.delete(controlRoot);
  }
  return summary;
}
